import React, { useState } from 'react';
import { logPartnerUsage } from '../../lib/api';

/** Pay-Per-Use logger + rollup chart for a partner dashboard. */
const UsageChart = ({ partnerId, byProduct, recentUsage = [], onLogged }) => {
  const [form, setForm] = useState({
    bottle_id: 'SH-042-0188',
    room_id: '',
    product_slug: 'ananta-radiance-serum',
    ml_dispensed: 3,
  });
  const [state, setState] = useState({ loading: false, err: '' });

  const update = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: k === 'ml_dispensed' ? Number(e.target.value) : e.target.value,
    }));

  const onLog = async (e) => {
    e.preventDefault();
    setState({ loading: true, err: '' });
    try {
      const res = await logPartnerUsage(partnerId, form);
      setState({ loading: false, err: '' });
      if (onLogged) onLogged(res);
    } catch (err) {
      setState({
        loading: false,
        err: err?.response?.data?.detail || 'Could not log usage.',
      });
    }
  };

  const maxMl = Math.max(1, ...byProduct.map((r) => r.ml));

  return (
    <section data-testid="partner-usage" className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-7" data-testid="usage-chart">
        <h3 className="font-serif text-2xl text-[#7C9CB1] font-light mb-6">
          Pay-Per-Use · by product
        </h3>
        {byProduct.length === 0 ? (
          <p className="font-serif italic text-[#90B0BF] py-6">
            No usage logged yet.
          </p>
        ) : (
          <ul className="space-y-3">
            {byProduct.map((row, i) => (
              <li key={row.product_slug} data-testid={`usage-bar-${i}`}>
                <div className="flex items-baseline justify-between font-mono text-[11px] text-[#7C9CB1]">
                  <span className="tracking-[0.18em] uppercase">
                    {row.product_slug}
                  </span>
                  <span>
                    {row.ml.toFixed(0)} ml · {row.events} events
                  </span>
                </div>
                <div className="mt-1 h-2 bg-[#C1D7E2]/40">
                  <div
                    className="h-full bg-[#7C9CB1] transition-[width] duration-500"
                    style={{ width: `${(row.ml / maxMl) * 100}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}

        {recentUsage.length > 0 && (
          <div className="mt-10">
            <h4 className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-4">
              Latest dispenses
            </h4>
            <ol className="divide-y divide-[#C1D7E2]">
              {recentUsage.slice(0, 5).map((u, i) => (
                <li
                  key={u.id || i}
                  data-testid={`usage-row-${i}`}
                  className="py-3 flex items-baseline justify-between gap-3"
                >
                  <span className="font-mono text-[11px] text-[#7C9CB1]">
                    {u.bottle_id} · {u.product_slug || '—'}
                    {u.room_id && ` · room ${u.room_id}`}
                  </span>
                  <span className="font-mono text-[11px] text-[#90B0BF]">
                    {u.ml_dispensed.toFixed(1)} ml ·{' '}
                    {new Date(u.created_at).toLocaleTimeString()}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      <form
        onSubmit={onLog}
        data-testid="usage-log-form"
        className="lg:col-span-5 p-8 border border-[#C1D7E2] bg-[#F4F8F9]"
      >
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-5">
          Log a dispense
        </p>
        <div className="space-y-4">
          <Field label="Bottle ID">
            <input
              required
              value={form.bottle_id}
              onChange={update('bottle_id')}
              data-testid="usage-bottle-id"
              className="usage-input"
            />
          </Field>
          <Field label="Room (optional)">
            <input
              value={form.room_id}
              onChange={update('room_id')}
              placeholder="R104"
              data-testid="usage-room-id"
              className="usage-input"
            />
          </Field>
          <Field label="Product">
            <select
              value={form.product_slug}
              onChange={update('product_slug')}
              data-testid="usage-product"
              className="usage-input"
            >
              {[
                'ananta-radiance-serum',
                'moonwater-cleansing-oil',
                'stillness-cream',
                'river-mist-tonique',
                'soul-night-oil',
                'first-light-ritual',
              ].map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>
          <Field label="ml dispensed">
            <input
              type="number"
              min={0.1}
              max={500}
              step={0.1}
              value={form.ml_dispensed}
              onChange={update('ml_dispensed')}
              data-testid="usage-ml"
              className="usage-input"
            />
          </Field>
        </div>
        <button
          type="submit"
          disabled={state.loading}
          data-testid="usage-submit"
          className="mt-6 bg-[#7C9CB1] text-[#F4F8F9] px-6 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#90B0BF] transition-colors disabled:opacity-60"
        >
          {state.loading ? 'Logging…' : 'Log event'}
        </button>
        {state.err && (
          <p data-testid="usage-error" className="mt-3 text-sm text-[#7C9CB1]">
            {state.err}
          </p>
        )}
      </form>

      <style>{`
        .usage-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(124,156,177,0.4);
          color: #7C9CB1;
          font-family: 'Outfit', sans-serif;
          font-weight: 300;
          padding: 8px 2px;
          font-size: 14px;
        }
        .usage-input:focus { outline: none; border-bottom-color: #7C9CB1; }
      `}</style>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
      {label}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

export default UsageChart;
